import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { themes, ThemeName, ThemeConfig } from '../lib/themes';

interface ThemeContextType {
  currentTheme: ThemeConfig;
  themeName: ThemeName;
  setTheme: (themeName: ThemeName) => void;
  availableThemes: typeof themes;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: ThemeName;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ 
  children, 
  defaultTheme = 'healing' 
}) => {
  const [themeName, setThemeName] = useState<ThemeName>(defaultTheme);
  const [currentTheme, setCurrentTheme] = useState<ThemeConfig>(themes[defaultTheme]);

  // Load theme from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('app-theme') as ThemeName;
    if (savedTheme && themes[savedTheme]) {
      setThemeName(savedTheme);
      setCurrentTheme(themes[savedTheme]);
    }
  }, []);

  // Apply CSS custom properties when theme changes
  useEffect(() => {
    const root = document.documentElement;
    const theme = themes[themeName];
    
    // Apply color variables
    Object.entries(theme.colors.primary).forEach(([key, value]) => {
      root.style.setProperty(`--color-primary-${key}`, value);
    });
    
    Object.entries(theme.colors.secondary).forEach(([key, value]) => {
      root.style.setProperty(`--color-secondary-${key}`, value);
    });
    
    Object.entries(theme.colors.neutral).forEach(([key, value]) => {
      root.style.setProperty(`--color-neutral-${key}`, value);
    });

    Object.entries(theme.colors.success).forEach(([key, value]) => {
      root.style.setProperty(`--color-success-${key}`, value);
    });

    Object.entries(theme.colors.error).forEach(([key, value]) => {
      root.style.setProperty(`--color-error-${key}`, value);
    });

    Object.entries(theme.colors.warning).forEach(([key, value]) => {
      root.style.setProperty(`--color-warning-${key}`, value);
    });
    
    // Apply other theme variables
    root.style.setProperty('--font-family', theme.typography.fontFamily);
    root.style.setProperty('--heading-weight', theme.typography.headingWeight);
    root.style.setProperty('--body-weight', theme.typography.bodyWeight);
    
    // Apply theme class to body for additional styling
    document.body.className = document.body.className.replace(/theme-\w+/g, '');
    document.body.classList.add(`theme-${themeName}`);
    
    setCurrentTheme(theme);
  }, [themeName]);

  const setTheme = (newThemeName: ThemeName) => {
    setThemeName(newThemeName);
    localStorage.setItem('app-theme', newThemeName);
  };

  const contextValue: ThemeContextType = {
    currentTheme,
    themeName,
    setTheme,
    availableThemes: themes,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};